import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const NavigationBar = ({ onLogout, userInfo, onBackToCompanyManagement }) => {
  const navigate = useNavigate();
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [showContactClientsModal, setShowContactClientsModal] = useState(false);
  const [clients, setClients] = useState([]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Helper function to check if user is a client
  const isClient = () => userInfo?.accountType === 'client' || userInfo?.accountType === 'super_user' || userInfo?.accountType === 'super_client' || userInfo?.accountType === 'founder';

  // Load admins for the selected company
  const loadAdmins = async () => {
    try {
      if (userInfo?.companyName) {
        const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${userInfo.companyName}/admins`);
        if (response.ok) {
          const data = await response.json();
          console.log("loading admins data:", data);
          setAdmins(data.users || []);
        }
      }
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  };

  // Load clients for the selected company (for admins)
  const loadClients = async () => {
    try {
      if (userInfo?.selectedCompany) {
        const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${userInfo.selectedCompany}/users`);
        if (response.ok) {
          const data = await response.json();
          setClients(data.users || data || []);
        }
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  useEffect(() => {
    if (isClient() && userInfo?.companyName) {
      loadAdmins();
    }
    if ((userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && userInfo?.selectedCompany) {
      loadClients();
    }
  }, [userInfo?.companyName, userInfo?.selectedCompany]);

  const handleEmailAllAdmins = () => {
    if (admins.length === 0) {
      alert('No administrators found for your company');
      return;
    }

    const emails = admins.map(admin => admin.username).join(',');
    const subject = encodeURIComponent('Question from StreamLaunch Client');
    const body = encodeURIComponent(`Hello,

I have a question regarding my account and company setup.

Company: ${userInfo?.companyName || 'N/A'}
User: ${userInfo?.username || 'N/A'}

Best regards`);

    window.open(`mailto:${emails}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleEmailAllClients = () => {
    if (clients.length === 0) {
      alert('No clients found for this company');
      return;
    }

    const emails = clients.map(client => client.userEmail).join(',');
    const subject = encodeURIComponent('Message from StreamLaunch');
    const body = encodeURIComponent(`Hello,

This is a message from your StreamLaunch administrator.

Company: ${userInfo?.selectedCompany || 'N/A'}
Admin: ${userInfo?.name || userInfo?.username || 'N/A'}

Best regards`);

    window.open(`mailto:${emails}?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <nav className="bg-white border-b border-gray-200 shadow-soft sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-xl font-bold text-gray-900">StreamLaunch</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {(userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && userInfo?.selectedCompany && onBackToCompanyManagement && (
              <button
                onClick={onBackToCompanyManagement}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200 border border-gray-300"
              >
                ← Back to Admin Dashboard
              </button>
            )}

            {userInfo?.accountType !== 'investor' && (
              <button
                onClick={() => handleNavigation('/home')}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200 border border-gray-300"
              >
                Dashboard
              </button>
            )}

            {/* Contact StreamLaunch button for clients */}
            {isClient() && (
              <button
                onClick={() => setShowContactUsModal(true)}
                className="px-4 py-2 text-sm font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors duration-200 border border-blue-300"
                title="Contact StreamLaunch administrators"
              >
                Contact StreamLaunch
              </button>
            )}

            {/* Contact Clients button for admins */}
            {(userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && userInfo?.selectedCompany && (
              <button
                onClick={() => setShowContactClientsModal(true)}
                className="px-4 py-2 text-sm font-medium text-green-700 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors duration-200 border border-green-300"
                title="Contact clients"
              >
                Contact Clients
              </button>
            )}

            <button
              onClick={onLogout}
              className="btn-secondary"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Contact StreamLaunch Modal */}
      {showContactUsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact StreamLaunch</h3>
            <p className="text-gray-600 mb-4">
              Get in touch with the StreamLaunch administrators assigned to your company.
            </p>

            {admins.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Available Administrators:</h4>
                <div className="space-y-2 mb-4">
                  {admins.map((admin, index) => (
                    <div key={admin.username} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {admin.displayName || admin.username}
                        </p>
                        <p className="text-xs text-gray-500">{admin.username}</p>
                        <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {admin.accountType}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent('Question from StreamLaunch Client');
                          const body = encodeURIComponent(`Hello ${admin.displayName || admin.username},

I have a question regarding my account and company setup.

Company: ${userInfo?.companyName || 'N/A'}
User: ${userInfo?.username || 'N/A'}

Best regards`);
                          window.open(`mailto:${admin.username}?subject=${subject}&body=${body}`, '_blank');
                        }}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        Email
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleEmailAllAdmins}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Email All Admins
                  </button>
                  <button
                    onClick={() => setShowContactUsModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-500 text-sm mb-4">No administrators found for your company.</p>
                <button
                  onClick={() => setShowContactUsModal(false)}
                  className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Clients Modal */}
      {showContactClientsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Clients</h3>
            <p className="text-gray-600 mb-4">
              Send an email to the clients of this company.
            </p>

            {clients.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Available Clients:</h4>
                <div className="space-y-2 mb-4">
                  {clients.map((client, index) => (
                    <div key={client.userEmail} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{client.userName || client.userEmail}</p>
                        <p className="text-xs text-gray-500">{client.userEmail}</p>
                      </div>
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent('Message from StreamLaunch');
                          const body = encodeURIComponent(`Hello ${client.userName || client.userEmail},\n\nThis is a message from your StreamLaunch administrator.\n\nCompany: ${userInfo?.selectedCompany || 'N/A'}\nAdmin: ${userInfo?.name || userInfo?.username || 'N/A'}\n\nBest regards`);
                          window.open(`mailto:${client.userEmail}?subject=${subject}&body=${body}`, '_blank');
                        }}
                        className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Email
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleEmailAllClients}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    Email All Clients
                  </button>
                  <button
                    onClick={() => setShowContactClientsModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-500 text-sm mb-4">No clients found for this company.</p>
                <button
                  onClick={() => setShowContactClientsModal(false)}
                  className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;